import { StateCreator } from "zustand";
import { supabase } from "../../supabase/supabaseClient";
import { SupabaseTableEnum } from "../../supabase/supabaseTypes";
import { RealtimeChannel } from "@supabase/supabase-js";
import { Logger } from "../../logger/Logger";
import { useRealtimeStore } from ".";

export interface RealtimeSubscriptionSlice {
	channels: RealtimeChannel[];
	onDeliveryChange: ((payload: any) => void) | null;
	setOnDeliveryChange: (cb: (payload: any) => void) => void;
	subscribe: () => void;
	unsubscribe: () => Promise<void>;
}

export const createRealtimeSubscriptionStore: StateCreator<
	RealtimeSubscriptionSlice
> = (set, get) => ({
	channels: [],
	onDeliveryChange: null,
	setOnDeliveryChange: (cb) => set({ onDeliveryChange: cb }),
	subscribe: () => {
		// Idempotent: a second call would overwrite state.channels and orphan the
		// previous Supabase subscriptions, causing every realtime INSERT to fire
		// the handler more than once (chat messages would appear duplicated).
		if (get().channels.length > 0) {
			return;
		}

		// TODO: this is not used currently but would be needed again once we re-establish threads/replies
		/* 		const channel = supabase
			.channel("replies_channel")
			.on(
				"postgres_changes",
				{
					event: "INSERT",
					schema: "public",
					table: SupabaseTableEnum.REPLIES,
					filter: `shared_id=eq.${activityId}`,
				},
				(payload) => {
					useRealtimeStore.getState().updateReplies(payload);
				}
			)
			.subscribe(); */

		const channel1 = supabase
			.channel("action_channel")
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: SupabaseTableEnum.MESSAGE_ACTIONS,
				},
				(payload) => {
					Logger.info("Message changes", payload);
					useRealtimeStore.getState().updateActionRealtime(payload);
				}
			)
			.subscribe((status, err) => {
				if (status === "SUBSCRIBED") {
					Logger.info("[realtime] Connected to realtime messages_channel!");
				} else {
					Logger.error(`[realtime] Error subscribing to messages_channel: ${err}`, {
						identifier: "messages_channel",
					});
				}
			});

		const channel3 = supabase
			.channel("message_channel")
			.on(
				"postgres_changes",
				{
					event: "INSERT",
					schema: "public",
					table: SupabaseTableEnum.MESSAGES,
				},
				(payload) => {
					Logger.info("Message changes", payload);
					useRealtimeStore.getState().updateMessagesRealtime(payload);
				}
			)
			.subscribe((status, err) => {
				if (status === "SUBSCRIBED") {
					Logger.info(
						"[realtime] Connected to realtime messages_channel!"
					);
				} else {
					Logger.error(
						`[realtime] Error subscribing to messages_channel: ${err}`,
						{
							identifier: "messages_channel",
						}
					);
				}
			});

		// Because the user might use multiple devices or multiple tabs, we update the read state in realtime
		const channel4 = supabase
			.channel("read_state_channel")
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "user_channel_read_state",
				},
				(payload) => {
					Logger.info("Read state changes", payload);
					useRealtimeStore.getState().updateChannelReadStateRealtime(payload);
				}
			)
			.subscribe((status, err) => {
				if (status === "SUBSCRIBED") {
					Logger.info("[realtime] Connected to realtime read_state_channel!");
				} else {
					Logger.error(`[realtime] Error subscribing to read_state_channel: ${err}`, {
						identifier: "read_state_channel",
					});
				}
			});

		const channel5 = supabase
			.channel("deliveries_channel")
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "deliveries",
				},
				(payload) => {
					Logger.info("Delivery changes", payload);
					get().onDeliveryChange?.(payload);
				}
			)
			.subscribe((status, err) => {
				if (status === "SUBSCRIBED") {
					Logger.info("[realtime] Connected to realtime deliveries_channel!");
				} else {
					Logger.error(`[realtime] Error subscribing to deliveries_channel: ${err}`, {
						identifier: "deliveries_channel",
					});
				}
			});

		set({
			channels: [channel1, channel3, channel4, channel5],
		});
	},

	unsubscribe: async () => {
		Logger.info("Unsubscribing..");
		const channels = get().channels;
		set({ channels: [] });

		await Promise.all(
			channels.map((c) => {
				c.unsubscribe();
			})
		);
	},
});
